/**
 * DatasetPreviewChart Component
 *
 * Shows a preview chart of the selected datapoints' historical data
 * to help users visualize what data will be included in the dataset.
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { format, isValid } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, LineChart as ChartIcon, AlertCircle } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from "recharts";
import { cn } from "@/shared/lib/utils";
import type { SelectedDatapoint, MeasurementFieldSelections } from "../../types/dataset";

// Color palette for multiple series
const COLORS = [
  "var(--primary)",
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
];

interface DatasetPreviewChartProps {
  // For UNS mode
  unsSelections?: Map<string, SelectedDatapoint>;
  // For raw mode
  rawSelections?: MeasurementFieldSelections;
  dataSourceId?: string;
  bucket?: string;
  // Time range
  startTime: Date;
  endTime: Date;
  className?: string;
}

export function DatasetPreviewChart({
  unsSelections,
  rawSelections,
  dataSourceId,
  bucket,
  startTime,
  endTime,
  className,
}: DatasetPreviewChartProps) {
  const validStart = isValid(startTime);
  const validEnd = isValid(endTime);
  const startKey = validStart ? startTime.toISOString() : "invalid-start";
  const endKey = validEnd ? endTime.toISOString() : "invalid-end";
  // Get field mapping IDs from UNS selections
  const mappingIds = useMemo(() => {
    if (unsSelections && unsSelections.size > 0) {
      return Array.from(unsSelections.keys());
    }
    return [];
  }, [unsSelections]);

  // Fetch historical data for UNS selections
  const { data: historicalData, isLoading, error } = useQuery({
    queryKey: ["dataset-preview", mappingIds.join(","), startKey, endKey],
    queryFn: async () => {
      if (mappingIds.length === 0) return [];
      if (!validStart || !validEnd) return [];

      console.log("[DatasetPreviewChart] Fetching data for mappings:", mappingIds.slice(0, 5), "time range:", startTime.toISOString(), "to", endTime.toISOString());

      const { data, error } = await supabase.functions.invoke("influxdb-query", {
        body: {
          historical_raw: true,
          mapping_ids: mappingIds.slice(0, 5), // Limit to 5 for preview
          startTime: startTime.toISOString(),
          endTime: endTime.toISOString(),
        },
      });

      if (error) {
        console.error("[DatasetPreviewChart] Edge function error:", error);
        throw error;
      }

      console.log("[DatasetPreviewChart] Received data:", data);
      return (data?.data as Record<string, unknown>[]) || [];
    },
    enabled: mappingIds.length > 0 && validStart && validEnd,
    staleTime: 30000, // 30 seconds
  });

  // Get field names for legend
  const fieldNames = useMemo(() => {
    if (unsSelections) {
      return Array.from(unsSelections.values())
        .slice(0, 5)
        .map((dp) => dp.fieldName);
    }
    if (rawSelections) {
      return Object.values(rawSelections).flat().slice(0, 5);
    }
    return [];
  }, [unsSelections, rawSelections]);

  // Transform data for the chart
  const chartData = useMemo(() => {
    if (!historicalData || historicalData.length === 0) {
      console.log("[DatasetPreviewChart] No historical data to transform");
      return [];
    }

    console.log("[DatasetPreviewChart] Transforming data, fieldNames:", fieldNames);
    console.log("[DatasetPreviewChart] First data point:", historicalData[0]);

    return historicalData
      .map((point: Record<string, unknown>) => {
        const timestamp = point.timestamp as string;
        const date = new Date(timestamp);
        if (!isValid(date)) {
          return null;
        }
        const time = date.getTime();
        const formattedTime = format(date, "HH:mm");

        return {
          ...point,
          time,
          formattedTime,
        };
      })
      .filter(
        (point): point is Record<string, unknown> & { time: number; formattedTime: string } =>
          point !== null
      );
  }, [historicalData, fieldNames]);

  // Check if we have valid selections
  const hasSelections = mappingIds.length > 0 ||
    (rawSelections && Object.keys(rawSelections).length > 0);

  if (!validStart || !validEnd) {
    return (
      <div className={cn("flex flex-col items-center justify-center h-full text-muted-foreground p-4", className)}>
        <ChartIcon className="h-10 w-10 mb-2 opacity-30" />
        <p className="text-sm text-center">Select a valid time range to preview data</p>
      </div>
    );
  }

  if (!hasSelections) {
    return (
      <div className={cn("flex flex-col items-center justify-center h-full text-muted-foreground p-4", className)}>
        <ChartIcon className="h-10 w-10 mb-2 opacity-30" />
        <p className="text-sm text-center">Select datapoints to preview data</p>
      </div>
    );
  }

  // For raw mode, we don't have the query infrastructure set up
  if (!unsSelections && rawSelections) {
    return (
      <div className={cn("flex flex-col items-center justify-center h-full text-muted-foreground p-4", className)}>
        <ChartIcon className="h-10 w-10 mb-2 opacity-30" />
        <p className="text-sm text-center">Preview available in Unified Namespace mode</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={cn("flex items-center justify-center h-full", className)}>
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    console.error("[DatasetPreviewChart] Query error:", error);
    return (
      <div className={cn("flex flex-col items-center justify-center h-full text-destructive p-4", className)}>
        <AlertCircle className="h-8 w-8 mb-2" />
        <p className="text-sm text-center">Failed to load preview data</p>
        <p className="text-xs text-center mt-1 opacity-70">{(error as Error).message}</p>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className={cn("flex flex-col items-center justify-center h-full text-muted-foreground p-4", className)}>
        <ChartIcon className="h-10 w-10 mb-2 opacity-30" />
        <p className="text-sm text-center">No data available for the selected time range</p>
      </div>
    );
  }

  return (
    <div className={cn("h-full w-full", className)}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <defs>
            {fieldNames.map((field, index) => (
              <linearGradient key={field} id={`color-${field}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={COLORS[index % COLORS.length]} stopOpacity={0.3} />
                <stop offset="95%" stopColor={COLORS[index % COLORS.length]} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis
            dataKey="formattedTime"
            tick={{ fontSize: 10 }}
            className="text-muted-foreground"
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={{ fontSize: 10 }}
            className="text-muted-foreground"
            tickLine={false}
            axisLine={false}
            width={40}
            tickFormatter={(value) => {
              if (typeof value === "number") {
                return value > 1000 ? `${(value / 1000).toFixed(1)}k` : value.toFixed(1);
              }
              return value;
            }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "var(--popover)",
              borderColor: "var(--border)",
              borderRadius: "8px",
              fontSize: "12px",
            }}
            labelStyle={{ color: "var(--popover-foreground)" }}
          />
          <Legend wrapperStyle={{ fontSize: "10px" }} />
          {fieldNames.map((field, index) => (
            <Area
              key={field}
              type="monotone"
              dataKey={field}
              name={field}
              stroke={COLORS[index % COLORS.length]}
              strokeWidth={1.5}
              fillOpacity={1}
              fill={`url(#color-${field})`}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export default DatasetPreviewChart;
